import { useEffect, useMemo, useState, useCallback } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, ShieldAlert, RotateCcw, Download, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { supabase } from "@/integrations/supabase/client";
import { getIsAdmin } from "@/lib/reports.functions";
import {
  getQaChecklist,
  setQaCheck,
  resetQaChecklist,
  type QaItemStatus,
} from "@/lib/qa-checklist.functions";

export const Route = createFileRoute("/_authenticated/admin_/qa-checklist")({
  head: () => ({
    meta: [
      { title: "QAチェックリスト | 管理コンソール" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: QaChecklistPage,
});

const CATEGORY_LABELS: Record<string, string> = {
  A: "A. 認証",
  B: "B. 動画生成",
  C: "C. 課金",
  D: "D. 安全対策",
  E: "E. UI/UX",
  F: "F. SEO/PWA",
  G: "G. 法務",
  H: "H. 通知",
  I: "I. 紹介機能",
  J: "J. 管理機能",
  K: "K. エラー処理",
};

function QaChecklistPage() {
  const isAdminFn = useServerFn(getIsAdmin);
  const getListFn = useServerFn(getQaChecklist);
  const setCheckFn = useServerFn(setQaCheck);
  const resetFn = useServerFn(resetQaChecklist);

  const [authChecked, setAuthChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  const [items, setItems] = useState<QaItemStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [activeCat, setActiveCat] = useState<string>("A");
  const [onlyUnchecked, setOnlyUnchecked] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});

  // Auth check
  useEffect(() => {
    (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData.session) {
          setIsAdmin(false);
          return;
        }
        setToken(sessionData.session.access_token);
        const res = await isAdminFn({
          headers: { Authorization: `Bearer ${sessionData.session.access_token}` },
        });
        setIsAdmin(res.isAdmin);
      } catch (e) {
        console.error("admin check failed", e);
        setIsAdmin(false);
      } finally {
        setAuthChecked(true);
      }
    })();
  }, [isAdminFn]);

  const loadList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await getListFn({ headers: { Authorization: `Bearer ${token}` } });
      setItems(data);
      setNoteDrafts((prev) => {
        const next: Record<string, string> = { ...prev };
        for (const it of data) {
          if (next[it.id] === undefined) next[it.id] = it.note ?? "";
        }
        return next;
      });
    } catch (e) {
      console.error(e);
      toast.error("チェックリストの読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  }, [getListFn, token]);

  useEffect(() => {
    if (isAdmin && token) void loadList();
  }, [isAdmin, token, loadList]);

  const categories = useMemo(() => {
    const set = new Set(items.map((i) => i.category));
    return Array.from(set).sort();
  }, [items]);

  const totalCount = items.length;
  const checkedCount = items.filter((i) => i.checked).length;
  const percent = totalCount === 0 ? 0 : Math.round((checkedCount / totalCount) * 100);

  const visibleItems = useMemo(() => {
    return items
      .filter((i) => i.category === activeCat)
      .filter((i) => (onlyUnchecked ? !i.checked : true));
  }, [items, activeCat, onlyUnchecked]);

  async function handleToggle(item: QaItemStatus, nextChecked: boolean) {
    if (!token) return;
    setSavingId(item.id);
    try {
      await setCheckFn({
        headers: { Authorization: `Bearer ${token}` },
        data: {
          item_id: item.id,
          checked: nextChecked,
          note: noteDrafts[item.id] ?? item.note ?? "",
        },
      });
      toast.success(nextChecked ? "チェックしました" : "チェックを外しました");
      await loadList();
    } catch (e) {
      console.error(e);
      toast.error("保存に失敗しました");
    } finally {
      setSavingId(null);
    }
  }

  async function handleSaveNote(item: QaItemStatus) {
    if (!token) return;
    setSavingId(item.id);
    try {
      await setCheckFn({
        headers: { Authorization: `Bearer ${token}` },
        data: {
          item_id: item.id,
          checked: item.checked,
          note: noteDrafts[item.id] ?? "",
        },
      });
      toast.success("メモを保存しました");
      await loadList();
    } catch (e) {
      console.error(e);
      toast.error("メモの保存に失敗しました");
    } finally {
      setSavingId(null);
    }
  }

  async function handleReset() {
    if (!token) return;
    try {
      await resetFn({ headers: { Authorization: `Bearer ${token}` } });
      toast.success("全項目をリセットしました");
      setNoteDrafts({});
      await loadList();
    } catch (e) {
      console.error(e);
      toast.error("リセットに失敗しました");
    }
  }

  function handleExportCsv() {
    const header = ["id", "category", "label", "checked", "checked_by", "checked_at", "note"];
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const rows = items.map((i) =>
      [
        i.id,
        i.category,
        i.label,
        i.checked ? "true" : "false",
        i.checked_by_name ?? "",
        i.checked_at ?? "",
        i.note ?? "",
      ]
        .map((v) => escape(String(v)))
        .join(","),
    );
    const csv = "\uFEFF" + [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `qa-checklist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!authChecked) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="min-h-screen bg-background px-4 py-16">
        <div className="mx-auto max-w-md">
          <Card className="p-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <ShieldAlert className="h-7 w-7 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold">アクセス権がありません</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              この画面は管理者ロールを持つユーザーのみが利用できます。
            </p>
            <Button asChild className="mt-6">
              <Link to="/dashboard">ダッシュボードへ</Link>
            </Button>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <Button asChild variant="ghost" size="sm" className="mb-2 -ml-3">
              <Link to="/admin">
                <ArrowLeft className="mr-1 h-4 w-4" />
                管理画面へ戻る
              </Link>
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">QAチェックリスト</h1>
            <p className="mt-2 text-muted-foreground">
              ローンチ前の最終確認項目。全管理者で共有されます。
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleExportCsv} disabled={loading}>
              <Download className="mr-2 h-4 w-4" />
              CSV出力
            </Button>
            <Button variant="destructive" onClick={() => setResetOpen(true)} disabled={loading}>
              <RotateCcw className="mr-2 h-4 w-4" />
              全リセット
            </Button>
          </div>
        </div>

        <Card className="mt-6 p-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm text-muted-foreground">進捗</p>
              <p className="text-2xl font-bold">
                {checkedCount} / {totalCount}
                <span className="ml-2 text-base text-muted-foreground">({percent}%)</span>
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={onlyUnchecked}
                onCheckedChange={(v) => setOnlyUnchecked(Boolean(v))}
              />
              未チェックのみ表示
            </label>
          </div>
          <Progress value={percent} className="mt-4" />
        </Card>

        <Tabs value={activeCat} onValueChange={setActiveCat} className="mt-6">
          <TabsList className="flex h-auto flex-wrap justify-start gap-1">
            {categories.map((c) => (
              <TabsTrigger key={c} value={c}>
                {c}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <h2 className="mt-6 text-xl font-semibold">{CATEGORY_LABELS[activeCat] ?? activeCat}</h2>

        {loading ? (
          <div className="mt-6 flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : visibleItems.length === 0 ? (
          <Card className="mt-6 p-8 text-center text-sm text-muted-foreground">
            表示できる項目がありません。
          </Card>
        ) : (
          <div className="mt-4 space-y-3">
            {visibleItems.map((item) => (
              <Card key={item.id} className="p-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id={`chk-${item.id}`}
                    checked={item.checked}
                    disabled={savingId === item.id}
                    onCheckedChange={(v) => void handleToggle(item, Boolean(v))}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <Label
                      htmlFor={`chk-${item.id}`}
                      className="cursor-pointer text-base font-medium"
                    >
                      <span className="mr-2 font-mono text-xs text-muted-foreground">
                        {item.id}
                      </span>
                      {item.label}
                    </Label>
                    {item.checked_by_name && item.checked_at && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {item.checked ? "✓ " : "○ "}
                        {item.checked_by_name} ・{" "}
                        {new Date(item.checked_at).toLocaleString("ja-JP")}
                      </p>
                    )}
                    <Textarea
                      placeholder="メモ（任意）"
                      value={noteDrafts[item.id] ?? ""}
                      onChange={(e) =>
                        setNoteDrafts((p) => ({ ...p, [item.id]: e.target.value }))
                      }
                      className="mt-2 min-h-[60px]"
                    />
                    <div className="mt-2 flex justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={
                          savingId === item.id ||
                          (noteDrafts[item.id] ?? "") === (item.note ?? "")
                        }
                        onClick={() => void handleSaveNote(item)}
                      >
                        メモを保存
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        title="全項目をリセットしますか?"
        description="すべての項目を未チェック状態に戻します。履歴は残りますが、現在のチェック状態は失われます。"
        confirmLabel="リセット実行"
        destructive
        onConfirm={handleReset}
      />
    </main>
  );
}

